import { Dispatch, ReactNode, SetStateAction } from 'react';

import { Box, Flex, Text } from '@chakra-ui/react';
import { Button } from '@mesh.ai/rabbit';
import ImageUpload from 'components/ImageUpload';
import { useToast } from 'components/hooks/useToast';
import { useSelector } from 'react-redux';
import { BulkUploadDownloadButton } from 'screens/admin/Employees/BulkUploadSidePanel/components/BulkUploadDownloadButton';
import { downloadBulkUploadOKRSampleFile, uploadOKRCSV } from 'screens/admin/Objectives/bulkUploadAccessControlApis';
import { okrsPageSelector } from 'selectors/okrsPage';

import { ArrowCircleUp, CloseIconChip, File } from 'assets/assetsV1/iconComponents';

import { Button as ButtonToolkitVersion } from 'uiToolkitV1/Button';
import { ProgressBar } from 'uiToolkitV1/ProgressBar';
import { TextBodyMedium, TextHeader1, TextHeader4, TextLabel } from 'uiToolkitV1/Typography';

import { useGoToMangeGoals } from '../hooks/useGoToMangeGoals';

export type InvalidFileError = {
  status: boolean;
  error?: string;
};

export type BulkUploadStatusType = {
  totalRecords?: number;
  recordsUpdated?: number;
  recordsFailed?: number;
  status?: 'IN_PROGRESS' | 'ERROR' | 'VALIDATING' | 'FINISHED';
  uuid?: number;
};

export type UploadFileProps = {
  setRefetchUploadStatusToggle: Dispatch<SetStateAction<boolean>>;
  selectedFile: File | null;
  setSelectedFile: Dispatch<SetStateAction<File>>;
  setInvalidFile: Dispatch<SetStateAction<InvalidFileError>>;
  isInvalidFile: InvalidFileError;
  selectUploadFile: (file: File) => void;
  csvWithErrorsUUID?: number | null;
  uploadingCSVFailed: boolean;
  handleFileAttached: () => void;
  handleFileUploadClicked: () => void;
  handleDownloadQuickGoalSampleFile: () => void;
  status: BulkUploadStatusType;
};

export type UploadingProgressUIProps = {
  totalRecords: number;
  recordsUpdated: number;
  handleDownloadQuickGoalSampleFile: () => void;
};

export type BulkUploadErrorUIProps = UploadFileProps;

export const MAX_FILE_SIZE = 10 * 1000 * 1000; // 10mb
export const MAX_FILE_SIZE_IN_MB = MAX_FILE_SIZE / (1000 * 1000); // 10mb

export const BasicSampleFileDownloadUI: React.FC = ({
  handleDownloadQuickGoalSampleFile,
}: {
  handleDownloadQuickGoalSampleFile: () => void;
}) => {
  const toast = useToast();

  return (
    <>
      {/*  @ts-expect-error : union too complex */}
      <Flex justifyContent="center" alignItems="center">
        <ButtonToolkitVersion
          sx={{
            height: 'auto!important',
            padding: '0px!important',
            marginRight: '4px',
            marginLeft: '4px',
            '.chakra-text': { fontSize: '14px', margin: '0px!important', lineHeight: '20px' },
          }}
          variant="tertiary"
          onClick={() => {
            handleDownloadQuickGoalSampleFile();

            downloadBulkUploadOKRSampleFile({ sampleType: 'BASIC' }).catch(() => {
              toast({
                title: 'An unknown error has occurred while downloading sample file',
                status: 'error',
              });
            });
          }}
        >
          Download
        </ButtonToolkitVersion>
        <TextBodyMedium color="#666"> a sample goal sheet.</TextBodyMedium>
      </Flex>
      <TextBodyMedium color="#666"> Upload file should be a .csv with maximum size 10MB</TextBodyMedium>
    </>
  );
};

export const UploadFileUI = ({
  setRefetchUploadStatusToggle,
  selectedFile,
  setSelectedFile,
  setInvalidFile,
  isInvalidFile,
  selectUploadFile,
  csvWithErrorsUUID,
  uploadingCSVFailed,
  handleFileAttached,
  handleFileUploadClicked,
  handleDownloadQuickGoalSampleFile,
  status,
}: UploadFileProps) => {
  const isError = isInvalidFile.status || csvWithErrorsUUID;
  const { recordsUpdated } = status;
  const { selectedTimelines } = useSelector(okrsPageSelector);
  const { startTS, endTS, timelineTag } = selectedTimelines?.[0] || [null, null, null];

  const handleSetFile = (file: File) => {
    selectUploadFile(file);
    handleFileAttached();
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      marginBottom="16px"
      width="100%"
      gap="8px"
    >
      <Box
        display="flex"
        padding="16px  24px"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        gap="12px"
        alignSelf="stretch"
        borderRadius="8px"
        background="#FFF"
      >
        {selectedFile && !uploadingCSVFailed && (
          <Box display="flex" padding="4px 8px" justifyContent="center" alignItems="center" gap="8px">
            <Box display="flex" justifyContent="center" alignItems="center" gap="2px">
              <File boxSize="20px" color="#666" />
            </Box>
            <TextBodyMedium color="#666">{selectedFile.name}</TextBodyMedium>
            <Box width="1px" height="16px" background="#CFD7DF" />
            <ButtonToolkitVersion
              variant="tertiary"
              sx={{
                height: 'auto!important',
                padding: '0px!important',
              }}
              onClick={() => {
                setSelectedFile(null);
                setInvalidFile({ status: false });
              }}
              StartIcon={CloseIconChip}
              startIconProps={{ boxSize: '16px', color: '#666!important' }}
            />
          </Box>
        )}

        {selectedFile &&
          isInvalidFile.status &&
          (isInvalidFile.error ? (
            <TextLabel color="#D42E1C">{isInvalidFile.error}</TextLabel>
          ) : (
            <Box display="flex" flexDirection="column">
              <TextLabel color="#D42E1C">This file size is too large to upload. The maximum</TextLabel>
              <TextLabel color="#D42E1C" textAlign="center">
                supported file size is {MAX_FILE_SIZE_IN_MB}MB.
              </TextLabel>
            </Box>
          ))}

        {uploadingCSVFailed && !isInvalidFile.status && csvWithErrorsUUID && (
          <Flex justifyContent="center" alignItems="center" flexDirection="column">
            <Flex justifyContent="center" alignItems="center">
              <TextLabel color="#D42E1C" whiteSpace="nowrap">
                {recordsUpdated > 0
                  ? 'Some data in the .csv was not valid, please'
                  : 'The data in the .csv was not valid, please'}
              </TextLabel>

              <BulkUploadDownloadButton
                uuid={csvWithErrorsUUID}
                fileName="MeshGuideCSV.csv"
                text="download"
                buttonStyles={{
                  height: 'auto',
                  padding: '0px!important',
                  marginRight: '4px',
                  marginLeft: '4px',
                  sx: {
                    '.chakra-text': { fontSize: '14px', margin: '0px!important', lineHeight: '20px' },
                  },
                }}
              />
            </Flex>
            <TextLabel color="#D42E1C">the file to see specific errors </TextLabel>
          </Flex>
        )}

        {selectedFile && !isInvalidFile.status && !uploadingCSVFailed ? (
          <Button
            variant="filled"
            // eslint-disable-next-line react/no-unstable-nested-components
            iconLeft={() => <ArrowCircleUp boxSize="24px" color="#fff" />}
            onClick={() => {
              handleFileUploadClicked();

              uploadOKRCSV(selectedFile, { startTS, endTS, timelineTag })
                .then((res) => {
                  console.log('upload csv res', res);
                  setRefetchUploadStatusToggle((prev) => !prev);
                })
                .catch((err) => {
                  setInvalidFile({ status: true, error: err.status === 400 ? err.message : undefined });
                });
            }}
          >
            Upload File{' '}
          </Button>
        ) : (
          <ImageUpload
            setFile={handleSetFile}
            accept=".csv"
            icon={
              // eslint-disable-next-line react/no-unstable-nested-components
              <Button variant="filled" iconLeft={() => <ArrowCircleUp boxSize="24px" color="#fff" />}>
                Choose File{' '}
              </Button>
            }
            disabled={false}
          />
        )}
      </Box>

      {!isError && <BasicSampleFileDownloadUI handleDownloadQuickGoalSampleFile={handleDownloadQuickGoalSampleFile} />}
    </Box>
  );
};

export const UploadingProgressUI = ({
  totalRecords = 1,
  recordsUpdated = 0,
  handleDownloadQuickGoalSampleFile,
}: UploadingProgressUIProps) => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    flexDirection="column"
    marginBottom="16px"
    width="100%"
    gap="8px"
  >
    <Box
      display="flex"
      padding="16px  24px"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      gap="12px"
      alignSelf="stretch"
      borderRadius="8px"
      background="#FFF"
    >
      <Box display="flex" flexDir="column" gap="4px" flex="1" width="100%" alignItems="center">
        <ProgressBar
          value={totalRecords === 0 ? 0 : (recordsUpdated / totalRecords) * 100}
          backgroundColor="#EBEFF3 !important"
          size="large"
          height="32px"
          color="linear-gradient(90deg, rgba(13, 129, 159, 0.24) 0%, #0D819F 171.86%)"
          text={undefined}
          textColor={undefined}
          textProps={undefined}
        />
        {recordsUpdated > 0 ? (
          <TextLabel color="#666">
            Goals uploaded:
            {/*  @ts-expect-error : union too complex */}
            <Text as="span" fontWeight="bold" marginLeft="4px">
              {recordsUpdated}
            </Text>{' '}
            of {totalRecords}
          </TextLabel>
        ) : (
          <TextLabel color="#666">Validating</TextLabel>
        )}
      </Box>
    </Box>

    <BasicSampleFileDownloadUI handleDownloadQuickGoalSampleFile={handleDownloadQuickGoalSampleFile} />
  </Box>
);

export const BulkUploadErrorUI = ({
  setRefetchUploadStatusToggle,
  selectedFile,
  setSelectedFile,
  setInvalidFile,
  isInvalidFile,
  selectUploadFile,
  csvWithErrorsUUID,
  uploadingCSVFailed,
  handleFileAttached,
  handleFileUploadClicked,
  handleDownloadQuickGoalSampleFile,
  status,
}: BulkUploadErrorUIProps) => {
  console.log('error ui');
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      marginBottom="16px"
      width="100%"
      gap="8px"
    >
      {uploadingCSVFailed && !csvWithErrorsUUID && (
        <TextBodyMedium color="#666">
          A server error occurred while uploading the file. Please try again.
        </TextBodyMedium>
      )}
      <UploadFileUI
        setRefetchUploadStatusToggle={setRefetchUploadStatusToggle}
        isInvalidFile={isInvalidFile}
        setInvalidFile={setInvalidFile}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        selectUploadFile={selectUploadFile}
        csvWithErrorsUUID={csvWithErrorsUUID}
        uploadingCSVFailed={uploadingCSVFailed}
        handleFileAttached={handleFileAttached}
        handleFileUploadClicked={handleFileUploadClicked}
        handleDownloadQuickGoalSampleFile={handleDownloadQuickGoalSampleFile}
        status={status}
      />
    </Box>
  );
};

export const BulkUploadSuccessUI = ({
  setShowUploadFileUI,
}: {
  setShowUploadFileUI: Dispatch<SetStateAction<boolean>>;
}) => {
  const goToManageGoals = useGoToMangeGoals();

  return (
    <Box>
      <TextBodyMedium color="#666">
        It can take few mins to a couple of hours to add goals to list view. You will be notified via email once upload
        is complete.{' '}
      </TextBodyMedium>
      <Box display="flex" justifyContent="center" alignItems="center" flex="1" width="100%" gap="16px" mt="16px">
        <Button
          variant="outline"
          onClick={() => {
            setShowUploadFileUI(true);
          }}
        >
          Upload another
        </Button>
        <Button variant="filled" onClick={goToManageGoals}>
          Back to manage goals
        </Button>
      </Box>
    </Box>
  );
};

export const BulkUploadHeading = ({ children, subHeading }: { children: ReactNode; subHeading?: string }) => (
  <Box display="flex" justifyContent="center" alignItems="center" marginBottom="16px" width="100%" flexDir="column">
    <TextHeader1 fontSize="20px" lineHeight="32px" fontWeight="600">
      {children}
    </TextHeader1>
    {subHeading && <TextHeader4 fontWeight="400">{subHeading}</TextHeader4>}
  </Box>
);
